import type { ProbationStatus } from '@/api/services/probation.service';
import { ProbationStatusBadge } from './ProbationStatusBadge';
import {
  daysUntil,
  formatDate,
  isActionable,
  PROBATION_STATUSES,
  relativeEnd,
} from './probationFormat';

/**
 * Compact probation summary for the employee profile. Renders nothing when
 * the profile carries no usable probation block, so older servers and
 * employees who never had a probation show no empty card.
 */
export function ProbationProfileCard({ probation }: { probation: unknown }): React.ReactElement | null {
  if (typeof probation !== 'object' || probation === null) return null;
  const value = probation as Record<string, unknown>;
  if (!PROBATION_STATUSES.includes(value.status as ProbationStatus)) return null;
  const status = value.status as ProbationStatus;
  const endDate = typeof value.currentEndDate === 'string' ? value.currentEndDate : '';
  const extensions =
    typeof value.extensionsCount === 'number' && Number.isFinite(value.extensionsCount)
      ? value.extensionsCount
      : 0;
  const days = daysUntil(endDate);
  const relative = isActionable(status) ? relativeEnd(days) : null;
  const overdue = relative !== null && days !== null && days < 0;

  return (
    <section
      aria-labelledby="profile-probation-heading"
      className="rounded-xl border border-gray-200 p-4 dark:border-gray-700"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3
          id="profile-probation-heading"
          className="text-base font-semibold text-gray-900 dark:text-gray-100"
        >
          Probation
        </h3>
        <ProbationStatusBadge status={status} />
      </div>
      <dl className="mt-3 grid gap-x-6 gap-y-3 sm:grid-cols-3">
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Ends
          </dt>
          <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
            {formatDate(endDate)}
            {relative && (
              <span
                className={
                  overdue
                    ? 'ml-1 text-xs font-medium text-danger'
                    : 'ml-1 text-xs text-gray-600 dark:text-gray-300'
                }
              >
                ({relative})
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Extensions
          </dt>
          <dd className="mt-1 text-sm tabular-nums text-gray-900 dark:text-gray-100">{extensions}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Review
          </dt>
          <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
            {value.reviewSubmitted === true ? 'Submitted' : 'Not submitted'}
          </dd>
        </div>
      </dl>
    </section>
  );
}
